import { addColorControlPanel } from '@/commons/TweakPanelHelper';
import {
    AdditiveBlending,
    BackSide,
    CustomBlending,
    DoubleSide,
    FrontSide,
    MeshPhysicalMaterial,
    MultiplyBlending,
    NoBlending,
    NormalBlending,
    type Blending,
    type MeshPhysicalMaterialParameters,
    SrcAlphaFactor,
    type BlendingDstFactor,
    ZeroFactor,
    OneFactor,
    SrcColorFactor,
    OneMinusSrcColorFactor,
    OneMinusSrcAlphaFactor,
    DstAlphaFactor,
    OneMinusDstAlphaFactor,
    DstColorFactor,
    OneMinusDstColorFactor,
    ConstantColorFactor,
    OneMinusConstantColorFactor,
    ConstantAlphaFactor,
    OneMinusConstantAlphaFactor,
    type BlendingSrcFactor,
    type BlendingEquation,
    AddEquation,
    SubtractEquation,
    ReverseSubtractEquation,
    MinEquation,
    MaxEquation,
    MeshBasicMaterial,
} from 'three';
import type { FolderApi } from 'tweakpane';

const meshPhysicalMatParam: MeshPhysicalMaterialParameters = {
    color: 0xc5bec3,
    transmission: 1,
    opacity: 0.67,
    attenuationColor: 0x9a9a9a,
    specularColor: 0x9a9a9a,
    blending: MultiplyBlending,
    blendSrc: ZeroFactor,
    blendDst: OneMinusConstantAlphaFactor,
    blendEquation: AddEquation,
    blendSrcAlpha: -0.5,
    metalness: 0.75,
    roughness: 0.86,
    envMapIntensity: 0.35,
    transparent: true,
    side: DoubleSide,
    thickness: 0.8,
    ior: 1,
    depthTest: false,
};

function customMaterialSetup(material: MeshBasicMaterial) {
    material.userData.backfaceIntensity = /* 1.0; */ 0.75;
    material.userData.noiseIntensity = 1.0;
    material.userData.noiseScale = 1.0;
    material.userData.time = 125;

    material.onBeforeCompile = (shader) => {
        shader.uniforms.backfaceIntensity = { value: material.userData.backfaceIntensity };
        shader.uniforms.noiseIntensity = { value: material.userData.noiseIntensity };
        shader.uniforms.noiseScale = { value: material.userData.noiseScale };
        shader.uniforms.time = { value: 0.0 };

        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            /* glsl */ `
                #include <common>
                varying vec2 vN;
                varying vec3 vWorldNormal;
                varying vec3 vWorldPosition;
            `,
        );
        shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            /* glsl */ `
                #include <begin_vertex>
                vN = uv;
                // World normal calculation inside world space
                vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
                vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
            `,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            /* glsl */ `
                #include <common>
                varying vec2 vN;
                varying vec3 vWorldNormal;
                varying vec3 vWorldPosition;

                uniform float backfaceIntensity;
                uniform float noiseIntensity;
                uniform float noiseScale;
                uniform float time;

                // Fonction de bruit simple
                float random(vec2 st) {
                    return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
                }

                float rand(float n){return fract(sin(n) * 43758.5453123);}

                float noise(float p){
                    float fl = floor(p);
                    float fc = fract(p);
                    return mix(rand(fl), rand(fl + 1.0), fc);
                }
            `,
        );
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <dithering_fragment>',
            /* glsl */ `
                #include <dithering_fragment>
                // point to camera position
                vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
                // Scalar product between camera position from point to point normal vector
                float normalDotView = dot(normalize(vWorldNormal), viewDirection);

                // if normal below zero then blend color frag with white to create transparency effect on backgside
                if (normalDotView < 0.0) {
                    vec3 blendColor = vec3(1.0, 1.0, 1.0);
                    gl_FragColor.rgb = mix(gl_FragColor.rgb, blendColor, backfaceIntensity);
                }
            `,
        );
    };
    material.needsUpdate = true;
    return material;
}

function createGlassDebugPanel(glassResearchFolder: FolderApi | undefined, physicalMaterial: MeshPhysicalMaterial) {
    if (!glassResearchFolder) return;
    glassResearchFolder.addButton({ title: 'log material' }).on('click', () => {
        console.info(physicalMaterial);
    });
    // Color
    const colorFolder = glassResearchFolder?.addFolder({ title: 'Color', expanded: false });
    addColorControlPanel(physicalMaterial, colorFolder);

    // alpha folder :
    const alphaFolder = glassResearchFolder?.addFolder({ title: 'Alpha', expanded: false });
    // opacity
    alphaFolder?.addBinding(
        {
            get opacity() {
                return physicalMaterial.opacity;
            },
            set opacity(value: number) {
                physicalMaterial.opacity = value;
                physicalMaterial.transparent = value < 1;
                physicalMaterial.needsUpdate = true;
            },
        },
        'opacity',
        {
            view: 'slider',
            label: 'Opacity',
            min: 0,
            max: 1,
            step: 0.01,
        },
    );
    // Alpha Test
    alphaFolder?.addBinding(physicalMaterial, 'alphaTest', {
        view: 'slider',
        label: 'Alpha Test',
        min: 0,
        max: 1,
        step: 0.01,
    });
    // blending
    alphaFolder?.addBinding(
        {
            get blending() {
                return physicalMaterial.blending;
            },
            set blending(value: Blending) {
                physicalMaterial.blending = value;
                physicalMaterial.needsUpdate = true;
            },
        },
        'blending',
        {
            view: 'select',
            options: { NoBlending, NormalBlending, AdditiveBlending, MultiplyBlending, CustomBlending },
        },
    );
    // blendSrc
    alphaFolder?.addBinding(
        {
            get blendSrc() {
                return physicalMaterial.blendSrc;
            },
            set blendSrc(value: BlendingSrcFactor) {
                physicalMaterial.blendSrc = value;
                physicalMaterial.needsUpdate = true;
            },
        },
        'blendSrc',
        {
            view: 'select',
            options: {
                ZeroFactor,
                OneFactor,
                SrcColorFactor,
                OneMinusSrcColorFactor,
                SrcAlphaFactor,
                OneMinusSrcAlphaFactor,
                DstAlphaFactor,
                OneMinusDstAlphaFactor,
                DstColorFactor,
                OneMinusDstColorFactor,
                ConstantColorFactor,
                OneMinusConstantColorFactor,
                ConstantAlphaFactor,
                OneMinusConstantAlphaFactor,
            },
        },
    );
    // blendDst
    alphaFolder?.addBinding(
        {
            get blendDst() {
                return physicalMaterial.blendDst;
            },
            set blendDst(value: BlendingDstFactor) {
                physicalMaterial.blendDst = value;
                physicalMaterial.needsUpdate = true;
            },
        },
        'blendDst',
        {
            view: 'select',
            options: {
                ZeroFactor,
                OneFactor,
                SrcColorFactor,
                OneMinusSrcColorFactor,
                SrcAlphaFactor,
                OneMinusSrcAlphaFactor,
                DstAlphaFactor,
                OneMinusDstAlphaFactor,
                DstColorFactor,
                OneMinusDstColorFactor,
                ConstantColorFactor,
                OneMinusConstantColorFactor,
                ConstantAlphaFactor,
                OneMinusConstantAlphaFactor,
            },
        },
    );
    // blendSrcAlpha
    alphaFolder?.addBinding(
        {
            get blendSrcAlpha() {
                if (physicalMaterial?.blendSrcAlpha === null) return -0.05;
                return physicalMaterial?.blendSrcAlpha;
            },
            set blendSrcAlpha(value: number | null) {
                if (value !== null && value < 0) {
                    physicalMaterial.blendSrcAlpha = null;
                    physicalMaterial.needsUpdate = true;
                    return;
                }
                physicalMaterial.blendSrcAlpha = value;
                physicalMaterial.needsUpdate = true;
            },
        },
        'blendSrcAlpha',
        {
            view: 'slider',
            label: 'blendSrcAlpha',
            min: -0.05,
            max: 5,
            step: 0.05,
        },
    );
    // blendEquation
    alphaFolder?.addBinding(
        {
            get blendEquation() {
                return physicalMaterial.blendEquation;
            },
            set blendEquation(value: BlendingEquation) {
                physicalMaterial.blendEquation = value;
                physicalMaterial.needsUpdate = true;
            },
        },
        'blendEquation',
        {
            view: 'select',
            options: {
                AddEquation,
                SubtractEquation,
                ReverseSubtractEquation,
                MinEquation,
                MaxEquation,
            },
        },
    );
    // blendEquationAlpha
    // alphaFolder?.addBinding(
    //     {
    //         get blendEquationAlpha() {
    //             // FIXME: issue here...
    //             return physicalMaterial?.blendEquationAlpha ?? AddEquation;
    //         },
    //         set blendEquationAlpha(value: BlendingEquation) {
    //             physicalMaterial.blendEquationAlpha = value;
    //             physicalMaterial.needsUpdate = true;
    //         },
    //     },
    //     'blendEquationAlpha',
    //     {
    //         view: 'select',
    //         options: {
    //             AddEquation,
    //             SubtractEquation,
    //             ReverseSubtractEquation,
    //             MinEquation,
    //             MaxEquation,
    //         },
    //     },
    // );

    glassResearchFolder?.addBinding(physicalMaterial, 'side', {
        label: 'Side',
        options: { FrontSide, BackSide, DoubleSide },
    });

    // Attenuation
    const attenuationFolder = glassResearchFolder?.addFolder({ title: 'Attenuation', expanded: false });
    addColorControlPanel(physicalMaterial, attenuationFolder);
    physicalMaterial.attenuationDistance = 5;
    attenuationFolder?.addBinding(physicalMaterial, 'attenuationDistance', {
        view: 'slider',
        label: 'Attenuation Distance',
        min: 0,
        max: 5,
        step: 0.01,
    });
}

export { createGlassDebugPanel, customMaterialSetup, meshPhysicalMatParam };
